import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  InputAdornment,
} from '@mui/material'
import { useNotify, useRefresh } from 'react-admin'
import { supabase } from '../supabaseClient'

interface Props {
  open: boolean
  onClose: () => void
  propertyId: string
  title: string
  currentArea: number
  totalPrice: number | null // cena całkowita — NIE zmienia się przy edycji metrażu
}

const plPln = new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN', maximumFractionDigits: 0 })

export const EditAreaModal: React.FC<Props> = ({ open, onClose, propertyId, title, currentArea, totalPrice }) => {
  const notify = useNotify()
  const refresh = useRefresh()
  const [value, setValue] = useState(String(currentArea ?? ''))
  const [saving, setSaving] = useState(false)

  // Akceptuj zarówno przecinek, jak i kropkę jako separator dziesiętny (PL vs format liczbowy).
  // Do RPC i tak idzie czysty Number, więc baza dostaje poprawny numeric niezależnie od wpisu.
  const newArea = Number(value.replace(/,/g, '.').trim())
  const valid = !Number.isNaN(newArea) && newArea > 0
  const changed = valid && newArea !== currentArea

  // Cena za m² = cena całkowita / metraż. Cena całkowita pozostaje bez zmian.
  const oldPerSqm = totalPrice && currentArea > 0 ? totalPrice / currentArea : null
  const newPerSqm = totalPrice && valid ? totalPrice / newArea : null

  async function confirm() {
    setSaving(true)
    const { data, error } = await supabase.rpc('update_property_area', {
      p_property_id: propertyId,
      p_new_area: newArea,
    })
    setSaving(false)
    const result = data as { success?: boolean; error?: string } | null
    if (error || result?.success === false) {
      notify(error?.message || result?.error || 'Błąd zapisu metrażu', { type: 'error' })
      return
    }
    notify('Zaktualizowano metraż', { type: 'success' })
    refresh() // odśwież listę — przeliczona cena za m²
    onClose()
  }

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Edycja metrażu — {title}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          label="Nowy metraż"
          fullWidth
          margin="dense"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          error={!!value && !valid}
          helperText={!!value && !valid ? 'Podaj liczbę większą od 0' : ' '}
          InputProps={{ endAdornment: <InputAdornment position="end">m²</InputAdornment> }}
        />

        {/* Confirm — wpływ na cenę za m² przed akceptacją */}
        <Alert severity="warning" sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
            Zmiana metrażu wpłynie na sposób liczenia ceny za m². Cena całkowita pozostaje bez zmian.
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 2, rowGap: 0.5 }}>
            <Typography variant="body2" color="text.secondary">Metraż:</Typography>
            <Typography variant="body2">
              {currentArea} m² → <strong>{valid ? `${newArea} m²` : '—'}</strong>
            </Typography>

            <Typography variant="body2" color="text.secondary">Cena za m²:</Typography>
            <Typography variant="body2">
              {oldPerSqm != null ? `${plPln.format(oldPerSqm)}/m²` : '—'} →{' '}
              <strong>{newPerSqm != null ? `${plPln.format(newPerSqm)}/m²` : '—'}</strong>
            </Typography>
          </Box>
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Anuluj</Button>
        <Button variant="contained" onClick={confirm} disabled={!changed || saving}>
          {saving ? 'Zapisywanie…' : 'Potwierdź zmianę'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
